/* eslint-disable react-hooks/exhaustive-deps */
import { faArrowRight, faXmark } from "@fortawesome/free-solid-svg-icons";
import { useFormikContext } from "formik";
import { cloneDeep } from "lodash";
import React, { useEffect, useState } from "react";
import { Accordion, Alert, Button, Modal, Tab, Table, Tabs } from "react-bootstrap";

import { RegimeTypeOptions, RegimeTypes } from "../../config/filingConstants";
import { ICRTableData, IChartData, IFormITR1 } from "../../config/interfaces";
import { InitDeductUndChapVIA } from "../../pages/itr-filing/efiling.config";
import { UtilService } from "../../services";
import FASvgIcon from "../FASvgIcon";
import PriceDisplay from "../PriceDisplay";
import { ContextAwareToggle } from "../accordion";
import CompareRegimesChart from "../charts/CompareRegimesChart";
import CompareRegimesTable from "../tables/CompareRegimesTable";
import ConfirmationModal from "./ConfirmationModal";

type Props = {
	showModal: boolean;
	handleModalClose: () => void;
	values: IFormITR1;
	message?: string;
	title?: string;
	onYesBtnText?: string;
};

const _CRTableData: ICRTableData = {
	GrossTotIncome: 0,
	newRegimeDeductions: 0,
	oldRegimeDeductions: 0,
	newRegimePayable: 0,
	oldRegimePayable: 0,
	newTaxableIncome: 0,
	oldTaxableIncome: 0,
	newBalTaxPayable: 0,
	oldBalTaxPayable: 0,
	newTotalTaxPaid: 0,
	oldTotalTaxPaid: 0
};

const CompareRegimesModal: React.FC<Props> = (props: Props) => {
	const {
		showModal,
		handleModalClose,
		values,
		title = "",
		onYesBtnText = "Ok, I Understand"
	} = props;

	const { setFieldValue } = useFormikContext<IFormITR1>();

	const [showChangeRegimeModal, setShowChangeRegimeModal] = useState<boolean>(false);
	const [CRTableData, setCRTableData] = useState<ICRTableData>(_CRTableData);
	const [CRChartData, setCRChartData] = useState<IChartData[]>([]);
	const [newRegimePayable, setNewRegimePayable] = useState<number>(0);
	const [payableOldRegime, setPayableOldRegime] = useState<number>(0);

	useEffect(() => {
		if (!showModal) {
			return;
		}

		const _SplRateIncome = values?.TotalCapitalGains || 0 - values.TotalNrlRateIncome;
		const _GrossTotalIncome = values.GrossTotIncome - _SplRateIncome;
		const _TotalSplRateTax = values.TotalSplRateTax;

		const _newRgmDeductions = Math.round(values.DeductUndChapVIA.Section80CCDEmployer);
		const _newRgmGTI = _GrossTotalIncome - _newRgmDeductions;
		const _newRgmNetTaxableIncome = Math.round(_newRgmGTI / 10) * 10; // Round Off to nearest 10
		const _newTaxDetails = UtilService.calculateNewRegimeTax(_newRgmNetTaxableIncome);
		const _newRgmResult = UtilService.getTaxAndTaxesPaidComputation(
			values,
			_newTaxDetails,
			RegimeTypes.NEW_TAX_REGIME,
			_newRgmNetTaxableIncome,
			_TotalSplRateTax
		);
		const _newBalTaxPayable = _newRgmResult.TaxPaidComputation.BalTaxPayable;
		const _newRegimePayable = Math.round(_newRgmResult.TaxComputation.TotTaxPlusIntrstPay);
		setNewRegimePayable(() => _newRegimePayable);
		const _newTotalTaxPaid = values.TaxPaid.TaxesPaid.TotalTaxesPaid;

		const _oldRgmDeductions = Math.round(values.TotalChapVIADeductions);
		const _oldRgmGTI = _GrossTotalIncome - _oldRgmDeductions;
		const _oldRgmNetTaxableIncome = Math.round(_oldRgmGTI / 10) * 10; // Round Off to nearest 10
		const _oldTaxDetails = UtilService.calculateOldRegimeTax(_oldRgmNetTaxableIncome, values.DOB);
		const _oldRgmResult = UtilService.getTaxAndTaxesPaidComputation(
			values,
			_oldTaxDetails,
			RegimeTypes.OLD_TAX_REGIME,
			_oldRgmNetTaxableIncome,
			_TotalSplRateTax
		);
		const _oldBalTaxPayable = _oldRgmResult.TaxPaidComputation.BalTaxPayable;
		const _oldRegimePayable = Math.round(_oldRgmResult.TaxComputation.TotTaxPlusIntrstPay);
		setPayableOldRegime(() => _oldRegimePayable);
		const _oldTotalTaxPaid = values.TaxPaid.TaxesPaid.TotalTaxesPaid;

		// chart related data preparation

		setCRChartData(() => [
			{
				name: "Taxable Income",
				NewRegimeValue: _newRgmNetTaxableIncome,
				OldRegimeValue: _oldRgmNetTaxableIncome
			},
			{
				name: "Tax Payable",
				NewRegimeValue: _newRegimePayable,
				OldRegimeValue: _oldRegimePayable
			}
		]);

		setCRTableData(() => ({
			GrossTotIncome: values.GrossTotIncome,
			newRegimeDeductions: _newRgmDeductions,
			oldRegimeDeductions: _oldRgmDeductions,
			newRegimePayable: _newRegimePayable,
			oldRegimePayable: _oldRegimePayable,
			newTaxableIncome: _newRgmNetTaxableIncome,
			oldTaxableIncome: _oldRgmNetTaxableIncome,
			newBalTaxPayable: _newBalTaxPayable,
			oldBalTaxPayable: _oldBalTaxPayable,
			newTotalTaxPaid: _newTotalTaxPaid,
			oldTotalTaxPaid: _oldTotalTaxPaid
		}));
	}, [showModal]);

	return (
		<>
			<Modal
				show={showModal}
				onHide={handleModalClose}
				centered
				backdrop="static"
				className="content-modal"
				size="lg"
			>
				<Button variant="primary" className="icon-only close-modal" onClick={handleModalClose}>
					<FASvgIcon icon={faXmark} />
				</Button>
				<Modal.Header className="justify-content-start">
					<Modal.Title className="text-start">{title}</Modal.Title>
				</Modal.Header>
				<Modal.Body className="pb-5">
					<Alert variant="primary" className="mb-4 justify-content-between">
						<h4 className="alert-title">
							Your Current Regime:{" "}
							<span className="text-primary">
								{RegimeTypeOptions[values.FilingReturnInfo.RegimeType]}
							</span>
						</h4>
						<Button variant="outline-primary" onClick={() => setShowChangeRegimeModal(() => true)}>
							Switch Regime
							<FASvgIcon icon={faArrowRight} />
						</Button>
					</Alert>
					<Accordion className="accordion-sm" defaultActiveKey="comparision-1">
						<Accordion.Item eventKey="comparision-1">
							<ContextAwareToggle eventKey="comparision-1" isChild={true}>
								<div className="btn-content justify-content-between">
									<span className="leading-para fw-medium">
										Detailed comparison of New and Old regime
									</span>
								</div>
							</ContextAwareToggle>
							<Accordion.Body className="pb-0">
								<hr />
								<Tabs
									defaultActiveKey="chart"
									id="comparision-tabs"
									className="border-nav-tabs mb-4"
								>
									<Tab eventKey="chart" title="Chart">
										<h3 className="text-center">
											We Recommend&nbsp;
											{payableOldRegime >= newRegimePayable ? "New Regime" : "Old Regime"}
										</h3>

										<p className="leading-para text-center text-greyDark">
											You'll be saving an additional&nbsp;
											<strong className="text-secondary">
												{payableOldRegime >= newRegimePayable ? (
													<PriceDisplay price={payableOldRegime - newRegimePayable} />
												) : (
													<PriceDisplay price={newRegimePayable - payableOldRegime} />
												)}
											</strong>
											&nbsp;in taxes
										</p>
										<CompareRegimesChart CRChartData={CRChartData} />
									</Tab>
									<Tab eventKey="stats" title="Statistics">
										<div className="rounded-4 overflow-hidden border border-1 border-greyMedium">
											<CompareRegimesTable CRTableData={CRTableData} />
										</div>
									</Tab>
								</Tabs>
							</Accordion.Body>
						</Accordion.Item>
						<Accordion.Item eventKey="comparision-2">
							<ContextAwareToggle eventKey="comparision-2" isChild={true}>
								<div className="btn-content justify-content-between">
									<span className="leading-para fw-medium">
										Key Differences between Old regime and New regime
									</span>
								</div>
							</ContextAwareToggle>
							<Accordion.Body className="pb-0">
								<hr />
								<div className="rounded-4 overflow-hidden border border-1 border-greyMedium">
									<Table responsive={true} striped={true} className="mb-0">
										<thead>
											<tr>
												<th className="cell-min-200"></th>
												<th className="cell-min-180 fw-medium">New Regime</th>
												<th className="cell-min-180 fw-medium">Old Regime</th>
											</tr>
										</thead>
										<tbody>
											<tr>
												<td>Slab Rates</td>
												<td>
													0 to 3 lakh - Exempt
													<br />
													3 to 6 lakhs - 5%
													<br />
													6 to 9 lakhs - 10%
													<br />
													9 to 12 lakhs - 15%
													<br />
													12 to 15 lakhs - 20%
													<br />
													above 15 lakhs - 30%
												</td>
												<td>
													0 to 2.5 lakh - Exempt
													<br />
													2.5 to 5 lakhs- 5%
													<br />
													5 to 10 lakhs - 20%
													<br />
													above 10 lakhs - 30%
												</td>
											</tr>
											<tr>
												<td>Standard deduction for Salary</td>
												<td>Applicable</td>
												<td>Applicable</td>
											</tr>
											<tr>
												<td>Professional Tax, HRA, LTA, Gratuity, Leave Encashment</td>
												<td>Not Applicable</td>
												<td>Applicable</td>
											</tr>
											<tr>
												<td>
													Section 24B (Home Loan Interest Deduction) for Self Occupied Property
												</td>
												<td>Not Applicable</td>
												<td>Applicable</td>
											</tr>
											<tr>
												<td>Section 24B (Home Loan Interest Deduction) for Rental Property</td>
												<td>Applicable</td>
												<td>Applicable</td>
											</tr>
											<tr>
												<td>Tax Saving deductions (excluding 80CCD(2), 80JJAA)</td>
												<td>Not Applicable</td>
												<td>Applicable</td>
											</tr>
											<tr>
												<td>80CCD(2), 80JJAA Deductions</td>
												<td>Applicable</td>
												<td>Applicable</td>
											</tr>
											<tr>
												<td>Rebate u/s 87A</td>
												<td>
													up to <PriceDisplay price={700000} decimalUpto={0} />
												</td>
												<td>
													up to <PriceDisplay price={500000} decimalUpto={0} />
												</td>
											</tr>
										</tbody>
									</Table>
								</div>
							</Accordion.Body>
						</Accordion.Item>
					</Accordion>
				</Modal.Body>
				<Modal.Footer className="modal-btns-wrap justify-content-end no-effect pt-4">
					<Button variant="primary" className="btn-w-160" onClick={handleModalClose}>
						{onYesBtnText}
					</Button>
				</Modal.Footer>
			</Modal>

			<ConfirmationModal
				showModal={showChangeRegimeModal}
				iconColorClass="text-danger"
				title="Are you sure?"
				message={
					<p>Do you want to change the Type of Regime, as it will change the flow of form.</p>
				}
				onYesBtnClick={() => {
					if (showChangeRegimeModal) {
						// when switching regime,
						// 1. From Old regime to New regime (else condition)
						// keep DeductUndChapVIA in TempDeductUndChapVIA and
						// update (reset) DeductUndChapVIA with initial values (along with allowed in both case)

						// 2. From New Regime to Old regime (if condition)
						// Simply, Keep the TempDeductUndChapVIA in DeductUndChapVIA, (just put the values filled in old regime earlier)

						let _InitDeductUndChapVIA = { ...InitDeductUndChapVIA };
						if (values.FilingReturnInfo.RegimeType === RegimeTypes.NEW_TAX_REGIME) {
							setFieldValue("FilingReturnInfo.RegimeType", RegimeTypes.OLD_TAX_REGIME);
							_InitDeductUndChapVIA = cloneDeep(values.TempDeductUndChapVIA);
						} else {
							setFieldValue("FilingReturnInfo.RegimeType", RegimeTypes.NEW_TAX_REGIME);
							setFieldValue("TempDeductUndChapVIA", cloneDeep(values.DeductUndChapVIA));
							_InitDeductUndChapVIA = {
								..._InitDeductUndChapVIA,
								Section80CCDEmployer: values.DeductUndChapVIA.Section80CCDEmployer
							};
						}

						const _TotalChapVIADeductions = Object.values(_InitDeductUndChapVIA).reduce(
							(curr, value) => curr + value,
							0
						);
						setFieldValue("DeductUndChapVIA", _InitDeductUndChapVIA);
						setFieldValue("TotalChapVIADeductions", _TotalChapVIADeductions);
						setFieldValue("LastSavedTimeStamp", new Date().getTime());
						setShowChangeRegimeModal(() => false);
						handleModalClose();
					}
				}}
				onNoBtnClick={() => setShowChangeRegimeModal(() => false)}
				onYesBtnText="Change"
			/>
		</>
	);
};

export default CompareRegimesModal;
